import { useEffect, useRef, useState } from "react";
import { useI18n } from "../../../i18n";
import { ZoomControls } from "../../../components/ZoomControls";
import { useOrbitDrag } from "../../../components/useOrbitDrag";
import { courseDeps } from "../../course";
import type { CourseViewLike } from "../../course";
import { StageGoalList, StagePico, StageSoundButton, StageStars, stageSound } from "../../stage";
import type { StageGoalLine } from "../../stage";
import type { MbFrame } from "../lib/live";
import type { SceneId } from "../lib/scenes";
import { actionsOf, bubbleOf, liveActions, poseAt } from "../lib/stageActions";
import type { MbAction } from "../lib/stageActions";
import { themeOfScene } from "../lib/stage3d";
import { templateKey } from "../lib/stageTemplate";
import {
  buildMbStage,
  MB_BODIES,
  MB_FLAT_BODIES,
  MB_FLAT_TF,
  MB_FOLLOW,
  MB_HERO_SIZE,
  MB_TF,
} from "../lib/stageBuild";
import type { StageObj } from "../lib/stageBuild";
import type { Ambience, MbTemplate } from "../lib/stageTemplate";
import "../../../styles/course.css";
import "../../../styles/stage.css";

/**
 * micro:bit 놀이터 3D 무대(2026-10-08 — 스마트브릭 기구 무대와 같은 렌더러 · 받침대 · 손님 · 별 · 소리 · 둘러보기).
 * 보드는 왼쪽(시뮬레이터 · 보드 거울) 그대로 — 무대 받침대에는 주인공 캐릭터가 서서 보드 조작을 따라 한다(lib/stageActions):
 * A 깡충 · B 한 바퀴 · A+B 크게 점프 · 흔들기 흔들흔들 · 로고 손 흔들기 · 기울기 그쪽으로 · 그림이 바뀌면 말풍선 · 새 음이면 음표.
 * 장면 유형별 템플릿(lib/stageTemplate — 4단계): 달리기 길(진행만큼 달림) · 격자 운동장(보물찾기 — 칸 걷기 · 전파 고리) · 그 밖은 받침대.
 * 센서 장면은 구역에 맞춰 무대 밝기 · 빛 색이 바뀐다(ambience).
 * 별이 늘면 손님이 환호하고 소리가 난다. 3D 를 못 그리면 onFail — 화면이 2D 띠로 돌아간다. 판정은 하지 않는다(별 · 목표 줄은 부르는 화면이 정한다).
 */
export function MbStage({
  scene,
  frame,
  stars,
  goals,
  pico,
  template,
  ambience,
  decor,
  modelUrl,
  onFail,
}: {
  scene: SceneId;
  frame: MbFrame | null;
  stars: number;
  goals: readonly StageGoalLine[];
  pico: string | null;
  template: MbTemplate;
  ambience: Ambience | null;
  /** 운영자 무대 꾸밈(카탈로그 stage_scene 'mb-<장면>' payload · 없으면 기본 장면) */
  decor?: unknown;
  /** 꾸밈의 올린 3D 모델 키 → GLB blob URL */
  modelUrl?: ((key: string) => Promise<string | null>) | null;
  onFail: () => void;
}) {
  const decorRef = useRef(decor);
  decorRef.current = decor;
  const modelUrlRef = useRef(modelUrl);
  modelUrlRef.current = modelUrl;
  const { t } = useI18n();
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bubbleRef = useRef<HTMLSpanElement>(null);
  const viewRef = useRef<CourseViewLike | null>(null);
  const heroRef = useRef<StageObj | null>(null);
  const templateRef = useRef(template);
  templateRef.current = template;
  const tKey = templateKey(template);
  const flat = template.kind !== "pedestal";
  const tf = flat ? MB_FLAT_TF : MB_TF;
  const tfRef = useRef(tf);
  tfRef.current = tf;
  const tick = useRef(0);
  const prevFrame = useRef<MbFrame | null>(null);
  const frameRef = useRef<MbFrame | null>(frame);
  frameRef.current = frame;
  const actions = useRef<MbAction[]>([]);
  const raf = useRef(0);
  const prevStars = useRef(stars);
  const excite = useRef(0);
  const [ready, setReady] = useState(false);
  const [bubble, setBubble] = useState<string | null>(null);
  const [muted, setMuted] = useState(stageSound.muted);
  const onFailRef = useRef(onFail);
  onFailRef.current = onFail;
  useOrbitDrag(canvasRef, () => viewRef.current, ready);

  /** 지금 몸짓 · 기울기를 주인공에 입히고 다시 그린다 — 몸짓이 남았으면 다음 그림도 */
  const step = () => {
    raf.current = 0;
    const hero = heroRef.current;
    const v = viewRef.current;
    if (!hero || !v) return;
    const now = performance.now();
    actions.current = liveActions(actions.current, now);
    const p = poseAt(actions.current, frameRef.current, now, MB_HERO_SIZE);
    hero.applyPose(p);
    const more = hero.animate(now / 1000);
    v.update({ tick: ++tick.current, transforms: tfRef.current });
    const b = bubbleOf(actions.current, now);
    setBubble(b);
    const at = b ? v.project?.(0, hero.lift) : null;
    if (bubbleRef.current && at)
      bubbleRef.current.style.transform = `translate(${at.x}px, ${at.y}px)`;
    if (p.moving || b) raf.current = requestAnimationFrame(step);
    // 템플릿만 도는 동안(보물찾기 전파 고리 · 달리기 마무리)은 초당 15장 — 학교 PC 부담을 줄인다
    else if (more)
      raf.current = window.setTimeout(() => {
        raf.current = requestAnimationFrame(step);
      }, 66);
  };
  const kick = () => {
    if (!raf.current) raf.current = requestAnimationFrame(step);
  };

  // 무대 — 장면이 바뀌면 다시 만든다(마운트마다 새 캔버스 — 같은 캔버스에 렌더러 둘이 붙지 않게)
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    let alive = true;
    const canvas = document.createElement("canvas");
    canvas.className = "pf-course__canvas pf-mb3d__canvas";
    box.prepend(canvas);
    canvasRef.current = canvas;
    setReady(false);
    void (async () => {
      try {
        const hero = await buildMbStage(templateRef.current);
        if (!alive) return hero.dispose();
        hero.update(templateRef.current);
        heroRef.current = hero;
        const v = await courseDeps().createView(canvas, {
          bodies: templateRef.current.kind === "pedestal" ? MB_BODIES : MB_FLAT_BODIES,
          followIndex: 0,
          me: { glbUrl: null, object: hero.object },
          stageScene: themeOfScene(scene),
          ...(decorRef.current != null ? { stageThemeData: decorRef.current } : {}),
          stageModelUrl: (k: string) => modelUrlRef.current?.(k) ?? Promise.resolve(null),
          follow: MB_FOLLOW,
          quality: "low",
        });
        if (!alive) return v.dispose();
        await v.ready;
        if (!alive) return v.dispose();
        viewRef.current = v;
        v.update({ tick: 0, transforms: tfRef.current });
        setReady(true);
        kick();
      } catch {
        if (alive) onFailRef.current();
      }
    })();
    return () => {
      alive = false;
      cancelAnimationFrame(raf.current);
      clearTimeout(raf.current);
      raf.current = 0;
      viewRef.current?.dispose();
      viewRef.current = null;
      heroRef.current?.dispose();
      heroRef.current = null;
      canvas.remove();
      canvasRef.current = null;
    };
    // kick · tf 는 ref 만 쓴다 · 템플릿 모양(tKey)이 바뀔 때만 다시 만든다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, tKey]);

  // 장면 상태(진행 · 자리 · 신호) → 템플릿
  useEffect(() => {
    if (!ready) return;
    heroRef.current?.update(template);
    kick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(template), ready]);

  // 운영자 꾸밈이 늦게 오거나 바뀌면 다시 꾸민다
  useEffect(() => {
    if (!ready || decor == null) return;
    viewRef.current?.setStageDecor?.(decor);
    kick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decor, ready]);

  // 센서 장면 구역 → 무대 밝기 · 빛 색
  const amb = ambience ? `${ambience.exposure}|${ambience.tint ?? ""}` : "";
  useEffect(() => {
    if (!ready) return;
    viewRef.current?.setAmbience?.(ambience ?? { exposure: 1, tint: null });
    kick();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amb, ready]);

  // 보드 조작 → 주인공 몸짓 · 입력이 있으면 손님이 신난다
  useEffect(() => {
    if (!ready) return;
    const now = performance.now();
    const add = actionsOf(prevFrame.current, frame, now);
    const tiltChanged =
      (prevFrame.current?.ax ?? 0) !== (frame?.ax ?? 0) ||
      (prevFrame.current?.ay ?? 0) !== (frame?.ay ?? 0);
    prevFrame.current = frame;
    if (add.length) actions.current = [...liveActions(actions.current, now), ...add];
    const active = add.length > 0;
    excite.current = active ? 1 : Math.max(0, excite.current - 0.1);
    viewRef.current?.stage?.setExcite(excite.current);
    if (active || tiltChanged) kick();
    // kick 은 ref 만 쓴다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frame, ready]);

  // 별이 늘면 손님 반응 · 소리
  useEffect(() => {
    if (stars > prevStars.current && ready) {
      viewRef.current?.stage?.react(stars >= 3 ? "cheer" : "star");
      if (stars >= 3) stageSound.fanfare();
      else stageSound.star();
      kick();
    }
    prevStars.current = stars;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stars, ready]);

  return (
    <div
      ref={boxRef}
      className="pf-course__scene pf-mb3d"
      data-view={ready ? "3d" : "loading"}
      data-testid="fs-mb-stage3d"
      role="img"
      aria-label={t("student.microbit.stage3d.label")}
    >
      <span
        ref={bubbleRef}
        className="pf-mb3d__bubble"
        hidden={!bubble}
        aria-hidden="true"
        data-testid="fs-mb-stage3d-bubble"
      >
        {bubble}
      </span>
      {ready && <ZoomControls getView={() => viewRef.current} wheelTarget={canvasRef} />}
      <StageStars stars={stars} />
      <StageGoalList goals={goals} />
      <StagePico line={pico} />
      <StageSoundButton
        muted={muted}
        onToggle={() => {
          stageSound.unlock();
          stageSound.setMuted(!muted);
          setMuted(!muted);
        }}
      />
    </div>
  );
}
